import { decodeJwtExp } from './jwt-utils';

/**
 * Pure decision helpers for IoT-token acquisition. No Angular dependencies so
 * they can be unit-tested in isolation (the auth service orchestrates the HTTP
 * calls and side effects on top of these).
 */

export type IotHttpClass = 'transient' | 'authRejected';

/**
 * Classify an HTTP failure from the IoT login/refresh endpoints.
 * - `transient`: network/CORS (status 0), 5xx, or unknown — safe to retry with
 *   the same credentials/token; never escalates to a re-auth prompt.
 * - `authRejected`: the credential/token was rejected (400/401/403) — the
 *   current rung cannot succeed, escalate to the next rung of the ladder.
 */
export function classifyHttpStatus(status: number | undefined): IotHttpClass {
  if (status === 400 || status === 401 || status === 403) return 'authRejected';
  return 'transient';
}

/** True when there is no token, it is within `marginMs` of expiry, or expired. */
export function needsRefresh(expiresAt: Date | null, now: number, marginMs: number): boolean {
  if (!expiresAt) return true;
  return expiresAt.getTime() - now <= marginMs;
}

/** Exponential backoff (1-indexed attempt) clamped to `maxMs`. */
export function nextBackoff(attempt: number, baseMs: number, maxMs: number): number {
  return Math.min(baseMs * Math.pow(2, Math.max(0, attempt - 1)), maxMs);
}

/**
 * Turn a `{ token }` login/refresh response into `{ token, expiresAt }`.
 * Returns null when no token is present. Falls back to a 7-day expiry when the
 * token's `exp` claim can't be parsed (matches the lambda's 1-week TTL).
 */
export function parseIotTokenResponse(
  resp: { token?: string } | null | undefined,
): { token: string; expiresAt: Date } | null {
  if (!resp?.token) return null;
  const parsed = decodeJwtExp(resp.token);
  if (parsed) return { token: resp.token, expiresAt: parsed };
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);
  return { token: resp.token, expiresAt };
}

/** Result of a single ladder rung (one /login or /refresh attempt). */
export type LadderRungResult =
  | { kind: 'ok'; token: string; expiresAt: Date }
  | { kind: 'transient' }
  | { kind: 'authRejected' };

/** Final outcome the auth service acts on. */
export type IotAcquireOutcome =
  | { kind: 'ok'; token: string; expiresAt: Date }
  | { kind: 'transient' }
  | { kind: 'permanent' };

export interface IotLadderDeps {
  /** Whether a stored username+password is available for the /login rung. */
  hasStoredCreds: boolean;
  /** POST /login with stored credentials. */
  login: () => Promise<LadderRungResult>;
  /** Fresh Cognito IdToken (refreshes the Cognito session); throws if signed out. */
  getIdToken: () => Promise<string>;
  /** POST /refresh with a Cognito IdToken (no password). */
  refresh: (idToken: string) => Promise<LadderRungResult>;
  log?: (event: string, details?: Record<string, any>) => void;
}

/**
 * Self-heal ladder for acquiring an IoT token. Pure orchestration (no side
 * effects) so it can be unit-tested with stubbed rungs:
 *   1. /login with stored credentials
 *   2. on auth-rejection or missing creds -> /refresh with a Cognito IdToken
 *   3. only if every automated path is exhausted -> `permanent` (prompt re-auth)
 *
 * A transient (network) failure on either rung returns `transient` so the
 * caller retries later; it never escalates to `permanent`.
 */
export async function runIotAcquireLadder(deps: IotLadderDeps): Promise<IotAcquireOutcome> {
  // Rung 1: /login with stored credentials.
  if (deps.hasStoredCreds) {
    const r = await deps.login();
    if (r.kind === 'ok') return { kind: 'ok', token: r.token, expiresAt: r.expiresAt };
    if (r.kind === 'transient') return { kind: 'transient' };
    deps.log?.('iot_login_rejected_try_refresh');
  }

  // Rung 2: /refresh with a fresh Cognito IdToken (no password required).
  try {
    const idToken = await deps.getIdToken();
    const r = await deps.refresh(idToken);
    if (r.kind === 'ok') return { kind: 'ok', token: r.token, expiresAt: r.expiresAt };
    if (r.kind === 'transient') return { kind: 'transient' };
  } catch (e) {
    deps.log?.('iot_refresh_no_session', { message: (e as Error)?.message });
  }

  // Every automated rung exhausted.
  return { kind: 'permanent' };
}
